import { configureStore, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AccountKey, IAccountOrders, IOrderHistory } from './types';
import { CmcWebApi } from '@cmctechnology/webinvest-api-client-web-trading';

export const ORDERS_STORE_NAME = `orders`;

export interface IOrdersStore {
  orders: Record<AccountKey, IAccountOrders>;
  history: Record<AccountKey, IOrderHistory>;
}

export const defaultOrdersStore: IOrdersStore = {
  orders: {},
  history: {}
};

export const createOrdersStore = (initialState: IOrdersStore) =>
  createSlice({
    name: ORDERS_STORE_NAME,
    initialState,
    reducers: {
      storeReset: () => defaultOrdersStore,

      ordersFetched: (store, action: PayloadAction<{ accountNo: AccountKey; hash: number; orders: CmcWebApi.Order.BaseOrderModel2[] }>) => {
        const orders = (store.orders[action.payload.accountNo] ??= { hash: 0, items: [] });

        orders.hash = action.payload.hash;
        orders.items = action.payload.orders;
      },

      orderHistoryFetched: (
        store,
        action: PayloadAction<{
          accountNo: AccountKey;
          from: number;
          to: number;
          pageSize: number;
          page: number;
          hash: number;
          pageCount: number;
          resultCount: number;
          items: CmcWebApi.Account.OrderHistorySummaryModel3[];
        }>
      ) => {
        const account = (store.history[action.payload.accountNo] ??= { from: {} });
        const from = (account.from[action.payload.from] ??= { to: {} });
        const to = (from.to[action.payload.to] ??= { pageSize: {} });
        const pageSize = (to.pageSize[action.payload.pageSize] ??= { pageCount: 0, resultCount: 0, page: {} });
        const page = (pageSize.page[action.payload.page] ??= { hash: 0, items: [] });

        if (pageSize.pageCount !== action.payload.pageCount) pageSize.pageCount = action.payload.pageCount;
        if (pageSize.resultCount !== action.payload.resultCount) pageSize.resultCount = action.payload.resultCount;
        page.hash = action.payload.hash;
        page.items = action.payload.items;
      }
    }
  });

export const { actions, reducer } = createOrdersStore(defaultOrdersStore);

export const createStore = (initialStore: IOrdersStore = defaultOrdersStore) =>
  configureStore({ reducer: { [ORDERS_STORE_NAME]: createOrdersStore(initialStore).reducer } });

export const OrdersStore = createStore();
export type IStore = ReturnType<typeof OrdersStore.getState>;
